import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { FirebaseTSAuth } from 'firebasets/firebasetsAuth/firebaseTSAuth';
import { FirebaseTSFirestore } from 'firebasets/firebasetsFirestore/firebaseTSFirestore';
import { getAuth, deleteUser } from 'firebase/auth';
import { first } from 'rxjs/operators';
import firebase from 'firebase/compat/app';

@Injectable({
  providedIn: 'root'
})
export class AccountDeletionService {
  private firestore = new FirebaseTSFirestore();
  private auth = new FirebaseTSAuth();

  constructor(
    private db: AngularFirestore,
    private afAuth: AngularFireAuth
  ) { }

  /**
   * Crée une demande de suppression de compte
   * @param userId ID de l'utilisateur demandant la suppression
   * @param reason Raison de la suppression
   * @param email Email de contact (facultatif)
   */
  async createDeletionRequest(userId: string, reason: string, email?: string): Promise<string> {
    try {
      // Créer un document de demande de suppression
      const requestRef = this.db.collection('accountDeletionRequests').doc();
      const requestId = requestRef.ref.id;

      await requestRef.set({
        userId: userId,
        reason: reason,
        contactEmail: email || '',
        status: 'pending', // pending, processing, completed, rejected
        createdAt: firebase.firestore.FieldValue.serverTimestamp(),
        updatedAt: firebase.firestore.FieldValue.serverTimestamp()
      });

      return requestId;
    } catch (error) {
      console.error('Erreur lors de la création de la demande de suppression:', error);
      throw error;
    }
  }

  /**
   * Vérifie si un utilisateur a une demande de suppression en attente
   * @param userId ID de l'utilisateur
   */
  async hasActiveDeletionRequest(userId: string): Promise<boolean> {
    try {
      const requests = await this.db.collection('accountDeletionRequests', 
        ref => ref.where('userId', '==', userId)
                  .where('status', 'in', ['pending', 'processing']))
        .get().pipe(first()).toPromise();

      return !requests.empty;
    } catch (error) {
      console.error('Erreur lors de la vérification des demandes existantes:', error);
      return false;
    }
  }

  /**
   * Supprime immédiatement le compte et les données associées
   * (Cette méthode serait appelée par un administrateur approuvant la demande)
   * @param userId ID de l'utilisateur à supprimer
   */
  async deleteUserAccount(userId: string): Promise<void> {
    try {
      // 1. Supprimer les documents de l'utilisateur dans Firestore
      await this.deleteUserData(userId);

      // 2. Supprimer le compte d'authentification Firebase
      const auth = getAuth();
      const currentUser = auth.currentUser;
      
      if (currentUser && currentUser.uid === userId) {
        await deleteUser(currentUser);
      } else {
        // Si l'administrateur supprime le compte d'un autre utilisateur
        // La suppression du compte authentification doit se faire côté serveur (Cloud Functions)
        console.log('La suppression du compte authentification nécessite des Cloud Functions');
      }
    } catch (error) {
      console.error('Erreur lors de la suppression du compte:', error);
      throw error;
    }
  }

  /**
   * Supprime toutes les données associées à un utilisateur dans Firestore
   * @param userId ID de l'utilisateur
   */
  private async deleteUserData(userId: string): Promise<void> {
    try {
      // Collections à nettoyer et leurs champs utilisateur correspondants
      const collectionsToClean = [
        { name: 'Users', field: 'id' },            // Profil utilisateur
        { name: 'Posts', field: 'creatorId' },     // Posts de l'utilisateur
        { name: 'Comments', field: 'userId' },     // Commentaires
        { name: 'Likes', field: 'userId' },        // Likes
        { name: 'Messages', field: 'senderId' },   // Messages envoyés
        // Messages reçus - requête supplémentaire
        { name: 'Messages', field: 'recipientId' },
        { name: 'Notifications', field: 'userId' } // Notifications
      ];

      // Supprimer les documents dans chaque collection
      for (const collection of collectionsToClean) {
        const querySnapshot = await this.db.collection(collection.name)
          .ref.where(collection.field, '==', userId).get();

        const batch = this.db.firestore.batch();
        querySnapshot.docs.forEach(doc => {
          batch.delete(doc.ref);
        });

        if (querySnapshot.docs.length > 0) {
          await batch.commit();
        }
      }

      console.log(`Données de l'utilisateur ${userId} supprimées avec succès`);
    } catch (error) {
      console.error('Erreur lors de la suppression des données:', error);
      throw error;
    }
  }
}
